import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FlaskConical } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const PARAGRAPHS = [
    ["overview", "Overview"],
    ["philosophy", "The thinking behind it"],
    ["why_selected", "Why these actives"],
    ["synergy", "How they work together"],
];

/** The product's own science record, pulled straight from CRM → The Science. */
export default function ProductScienceSummary({ slug }) {
    const { data } = useQuery({
        queryKey: ["product-science", slug],
        queryFn: async () => {
            const res = await fetch(`${BASE}/api/science/products/${slug}`);
            if (!res.ok) return null;
            return res.json();
        },
        enabled: !!slug,
        retry: false,
    });

    if (!data) return null;
    const paras = PARAGRAPHS.filter(([k]) => (data[k] || "").trim());
    const actives = (data.ingredients || []).slice(0, 8);
    if (!paras.length && !actives.length) return null;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-10 md:py-14" data-testid="product-science-summary">
            <div className="eyebrow mb-3 flex items-center gap-2">
                <FlaskConical className="w-3.5 h-3.5 text-gold" /> The science
            </div>
            <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tighter">
                {data.positioning || `Inside ${data.product?.name || "this formula"}`}
            </h2>

            <div className="mt-8 grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-5">
                    {paras.map(([key, label]) => (
                        <div key={key} data-testid={`science-para-${key}`}>
                            <div className="mono text-[9px] tracking-[0.2em] text-zinc-500">{label.toUpperCase()}</div>
                            <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{data[key]}</p>
                        </div>
                    ))}
                    <Link to={`/science/${slug}`} data-testid="science-read-more"
                        className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.2em] text-gold">
                        READ THE FULL SCIENCE <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                {!!actives.length && (
                    <div className="sci-card p-6 pt-7 self-start" data-testid="science-actives">
                        <span aria-hidden className="sci-rule" />
                        <div className="mono text-[9px] tracking-[0.2em] text-gold">
                            {(data.ingredients || []).length} ACTIVES STUDIED
                        </div>
                        <ul className="mt-4 space-y-2.5">
                            {actives.map((row) => (
                                <li key={row.name} className="flex items-baseline justify-between gap-3">
                                    <span className="text-sm">{row.name}</span>
                                    {row.dose && (
                                        <span className="mono text-[10px] tracking-[0.14em] text-zinc-500">
                                            {row.dose}{row.unit || "mg"}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                        {data.traditional_system && (
                            <div className="mt-5 mono text-[9px] tracking-[0.18em] text-zinc-500">
                                {data.traditional_system.toUpperCase()}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
